/**
 * MUI theme bridge.
 *
 * The portal's remaining MUI widgets (forms, dialogs, tables, menus) inherit
 * the same tokens as the TPC UI primitives: square corners, 1px borders, no
 * shadows, Brandon Grotesque, uppercase tracked controls, brand grey.
 */
import { createTheme, type Shadows } from "@mui/material/styles";

import { fontFamily, palette, status, tracking } from "./tokens";

const border = `1px solid ${palette.tpc200}`;

const shadows = Array(25).fill("none") as Shadows;

export const appTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: palette.tpc900,
      light: palette.tpc600,
      dark: palette.black,
      contrastText: palette.white,
    },
    secondary: {
      main: palette.tpc600,
      light: palette.tpc500,
      dark: palette.tpc800,
      contrastText: palette.white,
    },
    error: {
      main: status.red.dot,
      dark: "#b91c1c",
      light: "#f87171",
      contrastText: palette.white,
    },
    warning: {
      main: status.amber.dot,
      dark: "#b45309",
      light: "#fbbf24",
      contrastText: palette.tpc900,
    },
    info: {
      main: palette.tpc600,
      dark: palette.tpc800,
      light: palette.tpc400,
      contrastText: palette.white,
    },
    success: {
      main: status.green.dot,
      dark: "#047857",
      light: "#34d399",
      contrastText: palette.white,
    },
    text: {
      primary: palette.tpc800,
      secondary: palette.tpc500,
      disabled: palette.tpc400,
    },
    background: {
      default: palette.tpc50,
      paper: palette.white,
    },
    divider: palette.tpc200,
    action: {
      hover: palette.tpc100,
      selected: palette.tpc100,
      focus: palette.tpc100,
    },
  },
  shape: {
    borderRadius: 0,
  },
  shadows,
  typography: {
    fontFamily,
    fontWeightLight: 300,
    fontWeightRegular: 300,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontSize: 32, fontWeight: 500, textTransform: "uppercase", letterSpacing: tracking.head },
    h2: { fontSize: 26, fontWeight: 500, textTransform: "uppercase", letterSpacing: tracking.head },
    h3: { fontSize: 22, fontWeight: 500, textTransform: "uppercase", letterSpacing: tracking.head },
    h4: { fontSize: 20, fontWeight: 500, textTransform: "uppercase", letterSpacing: tracking.head },
    h5: { fontSize: 16, fontWeight: 500, textTransform: "uppercase", letterSpacing: tracking.head },
    h6: { fontSize: 14, fontWeight: 500, textTransform: "uppercase", letterSpacing: tracking.head },
    subtitle1: { fontSize: 15, fontWeight: 500 },
    subtitle2: { fontSize: 13, fontWeight: 500 },
    body1: { fontSize: 15, fontWeight: 300, lineHeight: 1.6 },
    body2: { fontSize: 14, fontWeight: 300, lineHeight: 1.6 },
    button: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: tracking.button,
      textTransform: "uppercase",
    },
    caption: { fontSize: 12, fontWeight: 300 },
    overline: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: tracking.eyebrow,
      textTransform: "uppercase",
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.tpc50,
          color: palette.tpc800,
        },
        "::selection": {
          background: palette.tpc600,
          color: palette.white,
        },
      },
    },

    /* ── surfaces ─────────────────────────────────────────────────── */
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none",
          boxShadow: "none",
        },
        outlined: { border },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          border,
          borderRadius: 0,
          boxShadow: "none",
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${palette.tpc200}`,
          borderRadius: 0,
          boxShadow: "none",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "16px 20px",
          borderBottom: border,
          fontSize: 16,
          fontWeight: 500,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: palette.tpc900,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: "20px" },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "12px 20px",
          borderTop: border,
          background: palette.tpc50,
          gap: 8,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: palette.white,
          borderLeft: border,
          borderRadius: 0,
          boxShadow: "none",
          "&.MuiDrawer-paperAnchorLeft": {
            borderLeft: 0,
            borderRight: border,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: palette.tpc50,
          color: palette.tpc800,
          boxShadow: "none",
          borderBottom: border,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.tpc200,
          "&.MuiDivider-withChildren": {
            marginTop: 24,
            lineHeight: 1.4,
            minHeight: 16,
            color: palette.tpc600,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            "&::before": { display: "none" },
            "&::after": { borderTop: `1px solid ${palette.tpc200}` },
            "& .MuiDivider-wrapper": { padding: "0 12px 0 0" },
          },
        },
      },
    },

    /* ── controls ─────────────────────────────────────────────────── */
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow: "none",
          padding: "8px 16px",
          minWidth: 0,
          fontWeight: 700,
          letterSpacing: tracking.button,
          textTransform: "uppercase",
          "&:hover": { boxShadow: "none" },
        },
        sizeLarge: { padding: "14px 24px" },
        sizeSmall: { padding: "5px 10px", fontSize: 11 },
        containedPrimary: {
          background: palette.tpc900,
          "&:hover": { background: palette.tpc600 },
        },
        containedSecondary: {
          background: palette.tpc600,
          "&:hover": { background: palette.tpc700 },
        },
        outlined: {
          borderColor: palette.tpc900,
          color: palette.tpc900,
          "&:hover": { background: palette.tpc100, borderColor: palette.tpc900 },
        },
        outlinedPrimary: {
          borderColor: palette.tpc900,
          color: palette.tpc900,
        },
        text: {
          color: palette.tpc600,
          "&:hover": { background: palette.tpc100, color: palette.tpc900 },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          color: palette.tpc600,
          "&:hover": { background: palette.tpc100, color: palette.tpc900 },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: { borderRadius: 0 },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontSize: 14, fontWeight: 300 },
        input: {
          "&::placeholder": { color: palette.tpc400, opacity: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: palette.tpc500,
        },
        shrink: { fontWeight: 700, color: palette.tpc600 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          background: palette.white,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.tpc300,
            borderRadius: 0,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.tpc500,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: palette.tpc600,
            borderWidth: 1,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: status.red.dot,
          },
        },
        input: { padding: "8px 12px" },
        inputSizeSmall: { padding: "6px 10px" },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: palette.tpc50,
          "&:hover": { backgroundColor: palette.tpc100 },
          "&.Mui-focused": { backgroundColor: palette.tpc100 },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: 12, marginLeft: 0 },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: { color: palette.tpc500 },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { borderRadius: 0 },
        select: { borderRadius: 0 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border,
          borderRadius: 0,
          boxShadow: "none",
          marginTop: 4,
        },
        list: { paddingTop: 4, paddingBottom: 4 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 14,
          minHeight: 36,
          borderRadius: 0,
          "&:hover": { background: palette.tpc50 },
          "&.Mui-selected": {
            background: palette.tpc100,
            "&:hover": { background: palette.tpc100 },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { border, borderRadius: 0, boxShadow: "none", marginTop: 4 },
        option: {
          fontSize: 14,
          borderRadius: 0,
          '&[aria-selected="true"]': { background: `${palette.tpc100} !important` },
        },
        inputRoot: { borderRadius: 0 },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { borderRadius: 0, color: palette.tpc400 },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: { color: palette.tpc400 },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 8 },
        thumb: { borderRadius: 0 },
        track: { borderRadius: 0, background: palette.tpc300 },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: { fontSize: 14 },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.tpc200}`,
          borderRadius: 0,
          background: palette.white,
          color: palette.tpc600,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          padding: "6px 12px",
          "&:hover": { background: palette.tpc50, borderColor: palette.tpc600 },
          "&.Mui-selected": {
            background: palette.tpc600,
            borderColor: palette.tpc600,
            color: palette.white,
            "&:hover": { background: palette.tpc700, borderColor: palette.tpc700 },
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { gap: 0 },
        grouped: {
          marginLeft: 0,
          "&:not(:first-of-type)": { borderLeft: `1px solid ${palette.tpc200}` },
          "&.Mui-selected + &.Mui-selected": { borderLeftColor: palette.tpc600 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          background: palette.tpc100,
          color: palette.tpc700,
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          height: 22,
        },
        label: { paddingLeft: 8, paddingRight: 8 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { background: palette.tpc900, height: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: palette.tpc600,
          minHeight: 40,
          padding: "8px 16px",
          "&.Mui-selected": { color: palette.tpc900 },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 0,
          background: palette.tpc900,
          fontSize: 12,
          fontWeight: 300,
          letterSpacing: "0.02em",
        },
        arrow: { color: palette.tpc900 },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          border,
          borderLeftWidth: 4,
          borderLeftStyle: "solid",
          fontSize: 14,
          fontWeight: 300,
          boxShadow: "none",
        },
        standardError: {
          background: status.red.bg,
          color: status.red.text,
          borderLeftColor: status.red.dot,
        },
        standardWarning: {
          background: status.amber.bg,
          color: status.amber.text,
          borderLeftColor: status.amber.dot,
        },
        standardSuccess: {
          background: status.green.bg,
          color: status.green.text,
          borderLeftColor: status.green.dot,
        },
        standardInfo: {
          background: palette.white,
          color: palette.tpc800,
          borderLeftColor: palette.tpc600,
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 0, backgroundColor: palette.tpc100 },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          borderRadius: "50%",
          background: palette.tpc100,
          color: palette.tpc600,
          fontWeight: 500,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: { borderRadius: 0, fontSize: 10 },
      },
    },

    /* ── tables ───────────────────────────────────────────────────── */
    MuiTableContainer: {
      styleOverrides: {
        root: { border, borderRadius: 0, boxShadow: "none", background: palette.white },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: { fontSize: 14 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background: palette.tpc600,
          "& .MuiTableCell-root": {
            color: palette.white,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            borderBottom: "none",
            whiteSpace: "nowrap",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${palette.tpc100}`,
          padding: "6px 12px",
          color: palette.tpc800,
        },
        head: { fontWeight: 600 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": { background: palette.tpc50 },
          "&:last-child .MuiTableCell-root": { borderBottom: "none" },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { fontSize: 12, color: palette.tpc500, borderTop: border },
        selectLabel: { fontSize: 12 },
        displayedRows: { fontSize: 12 },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: `${palette.white} !important`,
          fontSize: 11,
          "& .MuiTableSortLabel-icon": { color: `${palette.white} !important`, opacity: 0.6 },
        },
      },
    },

    /* ── navigation (Toolpad) ─────────────────────────────────────── */
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: "8px 12px",
          "&:hover": { background: palette.tpc100 },
          "&.Mui-selected": {
            background: palette.tpc100,
            borderLeft: `2px solid ${palette.tpc900}`,
            "&:hover": { background: palette.tpc100 },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: { minWidth: 32, color: palette.tpc500 },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: palette.tpc600,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: { minHeight: 56 },
      },
    },

    /* ── feedback ─────────────────────────────────────────────────── */
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          background: palette.tpc900,
          color: palette.white,
          fontSize: 14,
          boxShadow: "none",
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 0, height: 4, background: palette.tpc100 },
        bar: { borderRadius: 0 },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        circle: { strokeLinecap: "square" },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: "rgba(19, 21, 23, 0.45)" },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          border,
          boxShadow: "none",
          borderRadius: "0 !important",
          "&::before": { display: "none" },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { minHeight: 44, textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 12 },
      },
    },
  },
});
